import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { resetALL } from "../../../../../../../.globals/redux/actions";
import { endpoints } from "../../../../../../../core/defaultValues";
import {
  clientMutation,
  clientQuery,
} from "../../../../../../../core/data/GraphQLApi";
import { ExceptionManager } from "../../../../../../../core/logManager";

const INIT_STATE = {
  open: false,
  loading: null,
  crew: null,
  unit: null,
  error: null,
  id: null,
  updateManageCrewResult: null,
  crewsByUnit: [],
  crewsAvailableOnly: [],
  isAssignToUnit: false,
};

const route = "modules/manage/modals/createEditManageCrew/redux/slice";

export const manageCreateEditManageCrew = createAsyncThunk(
  "manage/createEditManageCrew/createEditManageCrew",
  async (payload, { rejectWithValue }) => {
    try {
      const mutation = `
              mutation (
                  ${payload.id ? "$id: Int!," : ""}
                  $firstName: String!,
                  $lastName: String!,
                  $unitId: Int
              ) {
                  data: ${payload.id ? "updateCrew" : "createCrew"} (
                      ${payload.id ? "id: $id," : ""}
                      firstName: $firstName,
                      lastName: $lastName,
                      unitId: $unitId
                  ) {
                      id
                      firstName
                      lastName 
                      unitId
                      unit {
                        id
                        label
                      }
                  }
              }
          `;

      const response = await clientMutation(
        mutation,
        payload,
        endpoints.GRAPHQL_GENERAL_V2
      );
      return response?.data;
    } catch (exc) {
      ExceptionManager(exc, route, "manageCreateEditManageCrew");
      return rejectWithValue(exc);
    }
  }
);

export const manageBulkUpdateManageCrew = createAsyncThunk(
  "manage/bulkUpdateManageCrew/bulkUpdateManageCrew",
  async (payload, { rejectWithValue }) => {
    try {
      const mutation = `
              mutation (
                 $unitId: Int!,
                 $crewIds: [Int]!,
              ) {
                  data: bulkUpdateCrew (
                      unitId: $unitId,
                      crewIds: $crewIds,
                  ) 
              }
          `;

      const response = await clientMutation(
        mutation,
        payload,
        endpoints.GRAPHQL_GENERAL_V2
      );
      return response?.data;
    } catch (exc) {
      ExceptionManager(exc, route, "manageBulkUpdateManageCrew");
      return rejectWithValue(exc);
    }
  }
);

export const manageSearchCrewsByUnit = createAsyncThunk(
  "manage/searchCreateEditManageCrew/searchCreateEditManageCrew",
  async ({ conditions, sort, limit = 0, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await clientQuery(
        `
            query ($conditions: [CrewSearchInput], $sort: [CrewSortInput], $limit: Int, $offset: Int) {
                data: searchCrews(
                    conditions: $conditions
                    sort: $sort
                    limit: $limit
                    offset: $offset
                ) {
                    total
                    items {
                        id
                        firstName
                        lastName 
                        unitId
                    }
                }
            }
        `,
        {
          conditions,
          sort,
          limit,
          offset,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );
      return response?.data || { items: [], total: 0 };
    } catch (exc) {
      ExceptionManager(exc, route, "searchCrews");
      return rejectWithValue(exc);
    }
  }
);

const manageCreateEditManageCrewRedux = createSlice({
  name: "manage/createEditManageCrew",
  initialState: INIT_STATE,
  reducers: {
    reset(state, action) {
      return action.payload
        ? { ...state, ...action.payload }
        : { ...INIT_STATE };
    },
    openModalCreateEditManageCrew(state, action) {
      state.open = true;
      state.crew = action?.payload?.crew;
      state.unit = action?.payload?.unit;
      state.isAssignToUnit = action?.payload?.isAssignToUnit;
    },
    openModalEditManageUnitCrew(state, action) {
      state.open = true;
      state.unit = action?.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetALL, () => INIT_STATE);

    //manageCreateEditManageCrew
    builder.addCase(manageCreateEditManageCrew.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(manageCreateEditManageCrew.fulfilled, (state, action) => {
      state.loading = false;
      state.updateManageCrewResult = action.payload;
    });
    builder.addCase(manageCreateEditManageCrew.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    //manageBulkUpdateManageCrew
    builder.addCase(manageBulkUpdateManageCrew.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(manageBulkUpdateManageCrew.fulfilled, (state, action) => {
      state.loading = false;
      state.updateManageCrewResult = action.payload;
    });
    builder.addCase(manageBulkUpdateManageCrew.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    //manageSearchCrewsByUnit
    builder.addCase(manageSearchCrewsByUnit.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(manageSearchCrewsByUnit.fulfilled, (state, action) => {
      state.loading = false;
      state.crewsAvailableOnly = action.payload.items;
    });
    builder.addCase(manageSearchCrewsByUnit.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = manageCreateEditManageCrewRedux;

// Extract and export each action creator by name
export const {
  reset,
  openModalCreateEditManageCrew,
  openModalEditManageUnitCrew,
} = actions;

// Export the reducer, either as a default or named export
export default reducer;
